import * as ListDataManager from 'dataContainer/listDataManager'

/**
 *
 * @param valueObj
 * @param rowObj
 * @param templateField
 * @param templateFields
 * @returns {*}
 */
export function transformConcatenation(
  valueObj,
  rowObj,
  templateField,
  templateFields,
  context,
  dataContainer,
  readOnlyTransform,
) {
  if (readOnlyTransform) return valueObj.cell_value

  const concatenationGovernance = templateField.field.concatenation_governance

  const pieces = concatenationGovernance.concatenation_governance_patterns

  // sort the pieces by precedence. the array order might not be correct
  pieces.sort((a, b) => {
    return a.precedence - b.precedence
  })

  const result = pieces
    .map(governancePattern => {
      return getValueForGovernancePattern(governancePattern, rowObj, templateFields)
    })
    .filter(data => !!data)
    .join(concatenationGovernance.separator)

  return result
}

function getValueForGovernancePattern(governancePattern, rowObj, templateFields) {
  if (governancePattern.concatenation_field_id) {
    return getFieldValue(governancePattern, rowObj)
  }
  if (governancePattern.concatenation_list_id) {
    return getListValue(governancePattern, rowObj, templateFields)
  }
  return ''
}

/**
 * Gets the value to concat for direct field mappings
 *
 * @param governancePattern
 * @param rowObj
 * @returns {null|*}
 */
function getFieldValue(governancePattern, rowObj) {
  const fieldId = governancePattern.concatenation_field_id
  const fieldValueObj = rowObj[fieldId] || {}
  return fieldValueObj.cell_value
}

/**
 * Gets the value to concat for list mappings.
 *
 * Attempts first to find if a field has the necessary mapping.
 * If not, it tries to find the necessary mapping as a dependent list
 * If those both fail, it warns and returns.
 *
 * @param governancePattern
 * @param rowObj
 * @param templateFields
 * @returns {*}
 */
function getListValue(governancePattern, rowObj, templateFields) {
  // try to find as each type
  const cellValue =
    findAsBoundField(governancePattern, rowObj, templateFields) ||
    findAsDependentListField(governancePattern, rowObj, templateFields) ||
    null

  if (cellValue) {
    return cellValue
  }

  console.warn('[CLARAVINE] Unable to find list value for concat', governancePattern, rowObj)

  return null
}

function findAsBoundField(governancePattern, rowObj, templateFields) {
  const listId = governancePattern.concatenation_list_id
  const columnId = governancePattern.concatenation_list_column_id

  // get any columns that are mapped to the same list and column
  const valueCandidate = templateFields.find(tf => {
    const listGovernance = getListGovernance(tf)
    return (
      listGovernance &&
      listGovernance.list_id === listId &&
      listGovernance.list_column_id === columnId
    )
  })

  // if nothing exists, exit prematurely
  if (!valueCandidate) {
    return null
  }

  const valueObj = rowObj[valueCandidate.field_id] || {}
  return valueObj.cell_value
}

function findAsDependentListField(governancePattern, rowObj, templateFields) {
  const listId = governancePattern.concatenation_list_id
  const listColumnId = governancePattern.concatenation_list_column_id

  // get the first column that is mapped to the same list
  // we don't really care which, because we are only worried about
  // dependent lists, which will have equivalent row numbers
  const dependentListColumn = templateFields.find(tf => {
    const listGovernance = getListGovernance(tf)
    return (
      listGovernance &&
      listGovernance.list_id === listId &&
      listGovernance.lists &&
      listGovernance.lists.type === 'dependent'
    )
  })

  // if nothing is tied to the same list, then nothing we can do about it
  if (!dependentListColumn) {
    return null
  }

  // get the current value for searching for the row number
  const selectedListValueObj = rowObj[dependentListColumn.field_id] || {}
  const selectedListValue = selectedListValueObj.cell_value

  // if there is no value, then we can't find the row number
  if (!selectedListValue) {
    return null
  }

  // get the row number for the current value of the column
  const dependentListGovernance = getListGovernance(dependentListColumn)
  const listData = ListDataManager.getListData(dependentListGovernance.list_id)
  const { listValues } = listData

  const dependentListValue = listValues.find(listValue => {
    return (
      listValue.list_id === dependentListGovernance.list_id &&
      listValue.list_column_id === dependentListGovernance.list_column_id &&
      listValue.value === selectedListValue
    )
  })

  // get the row number from the found value
  const dependentListValueRowNumber = dependentListValue ? dependentListValue.row_number : -1

  // find the value for the concat's listId, columnId, and rowNumber
  const concatValueForRowNumber = listValues.find(listValue => {
    return (
      listValue.list_id === listId &&
      listValue.list_column_id === listColumnId &&
      listValue.row_number === dependentListValueRowNumber
    )
  })

  // return the new value
  return concatValueForRowNumber ? concatValueForRowNumber.value : null
}

function getListGovernance(templateField) {
  const govOwner =
    templateField.field.segment_type === 'variable' ? templateField : templateField.field
  return govOwner.list_governance
}
